import React from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { BaseRouteParams } from 'hocs'
import { usePermission } from 'admin/hocs'
import { useHasFeatureFunc } from 'utils/useHasFeature'
import { UnderlineNavigationTabs } from 'uiComponents/navigation/underlineNavigationTabs'
import InSectionTabsSlot from 'uiComponents/navigation/sectionTabsSlot'

interface VenueSection {
    name: string
    path: string
    activeKey: string
    shouldShow: boolean
}

export function useVenueSections(): VenueSection[] {
    const { accountSlug } = useParams<BaseRouteParams>()
    const { hasPermission } = usePermission()
    const hasFeature = useHasFeatureFunc()

    const venue = `/account/${accountSlug}/venue`
    const canAccessCodes = hasPermission('access_codes', accountSlug)
    const reservationPoolsOn = hasFeature('RetailersReservationCodes')

    return [
        {
            name: 'Opening Times',
            path: `${venue}/opening_times`,
            activeKey: '/venue/opening_times',
            shouldShow: hasPermission('partner_admin', accountSlug),
        },
        {
            name: 'Ticket barcodes',
            path: `${venue}/codes/tickets/list/`,
            activeKey: '/venue/codes/tickets',
            shouldShow: canAccessCodes,
        },
        {
            name: 'Reservation codes',
            path: `${venue}/codes/reservations/${reservationPoolsOn ? 'list/' : ''}`,
            activeKey: '/venue/codes/reservations',
            shouldShow: canAccessCodes && hasFeature('BarcodeCheck'),
        },
        {
            name: 'Season passes',
            path: `${venue}/codes/sold_tickets/`,
            activeKey: '/venue/codes/sold_tickets',
            shouldShow: canAccessCodes && hasFeature('SeasonPassesUploadFeature'),
        },
        {
            name: 'Product IDs',
            path: `${venue}/codes/product_ids/`,
            activeKey: '/venue/codes/product_ids',
            shouldShow: canAccessCodes,
        },
        {
            name: 'Park Map',
            path: `${venue}/park_map`,
            activeKey: '/venue/park_map',
            shouldShow: hasFeature('parkMap'),
        },
    ].filter((section) => section.shouldShow)
}

function VenueSectionTabs() {
    const location = useLocation()
    const history = useHistory()
    const sections = useVenueSections()

    const tabs = sections.map((section) => ({
        name: section.name,
        current: location.pathname.includes(section.activeKey),
        href: section.path,
        onClick: () => history.push(section.path),
    }))

    if (tabs.length < 2) {
        return null
    }

    return (
        <InSectionTabsSlot>
            <UnderlineNavigationTabs tabs={tabs} />
        </InSectionTabsSlot>
    )
}

export default VenueSectionTabs
